/**
 * Single source of truth for menu-flow absolute header band heights vs list spacer.
 * Keep in sync with `MenuFlowHeader` / `MenuFlowHeaderSet2` chrome — never pad safe-area twice
 * (see `.cursor/rules/menu-flow-safe-area.mdc`).
 */
import { MENU_FLOW_HEADER_DATE_BLOCK_HEIGHT } from '../../shared/theme/sharedStyles';
import { MENU_FLOW_COUNTERS_BLOCK_HEIGHT } from './MenuFlowCountersRow';
import { SECTION_HEADER_HEIGHT } from './MenuFlowSectionHeader';

/** Studio set1/set3 inter-block gap (date→counters, counters→food type via section mt). */
export const MENU_FLOW_STUDIO_BLOCK_GAP = 8;
export const MENU_FLOW_STUDIO_FOOD_TYPE_HEIGHT = 38;
/** Outer section chip band (inner `SECTION_HEADER_HEIGHT` is 30). */
export const MENU_FLOW_STUDIO_SECTION_BAND_HEIGHT = 38;

/**
 * Expanded / collapsed content-band heights for studio chrome.
 * Safe area is owned by DATE_BLOCK (expanded) / shell `paddingTop` (collapsed) — not added here.
 */
export function resolveMenuFlowStudioHeaderHeights(): {
  fullHeaderHeight: number;
  collapsedHeaderHeight: number;
} {
  const g = MENU_FLOW_STUDIO_BLOCK_GAP;
  const date = MENU_FLOW_HEADER_DATE_BLOCK_HEIGHT;
  const counters = MENU_FLOW_COUNTERS_BLOCK_HEIGHT;
  const food = MENU_FLOW_STUDIO_FOOD_TYPE_HEIGHT;
  const section = MENU_FLOW_STUDIO_SECTION_BAND_HEIGHT;
  /** date + gap + counters + gap + food + gap + section (three gaps only). */
  const fullHeaderHeight = date + g + counters + g + food + g + section + g;
  const collapsedHeaderHeight = counters + g + food + g + section;
  return { fullHeaderHeight, collapsedHeaderHeight };
}

export const MENU_SET2_MONTH_ROW_HEIGHT = 20;
export const MENU_SET2_DATE_SLIDER_HEIGHT = 60;
export const MENU_SET2_DATE_SLIDER_MARGIN_BOTTOM = 8;
export const MENU_SET2_COUNTERS_ROW_HEIGHT = 40;
/** Date-slider bottom margin + counters row (~40). */
export const MENU_SET2_MAIN_BLOCK_EXTRA =
  MENU_SET2_DATE_SLIDER_MARGIN_BOTTOM + MENU_SET2_COUNTERS_ROW_HEIGHT;
export const MENU_SET2_SECTION_HEADER_GAP = 8;
/** Breathing room below the sticky food-type chip (expanded). */
export const MENU_SET2_SECTION_HEADER_BOTTOM = 12;
/** Bottom pad under chip when collapsed — enough for green to clear the bar. */
export const MENU_SET2_SECTION_HEADER_BOTTOM_COLLAPSED = 8;
/**
 * In-list section chip row — equal gap above/below chip. Must match FlatList getItemLayout (set2 only).
 */
export const MENU_SET2_SECTION_LIST_HEADER_ROW =
  MENU_SET2_SECTION_HEADER_GAP * 2 + SECTION_HEADER_HEIGHT;

/**
 * set2 content-band heights (safe area is outer wrap `paddingTop` + list spacer separately).
 * Collapsed = counters + section chip band only (month / date slider are height 0).
 */
export function resolveMenuFlowSet2HeaderHeights(): {
  fullHeaderHeight: number;
  collapsedHeaderHeight: number;
} {
  const section_band_expanded =
    MENU_SET2_SECTION_HEADER_GAP + SECTION_HEADER_HEIGHT + MENU_SET2_SECTION_HEADER_BOTTOM;
  const section_band_collapsed =
    MENU_SET2_SECTION_HEADER_GAP + SECTION_HEADER_HEIGHT + MENU_SET2_SECTION_HEADER_BOTTOM_COLLAPSED;
  const fullHeaderHeight =
    MENU_SET2_MONTH_ROW_HEIGHT +
    MENU_SET2_DATE_SLIDER_HEIGHT +
    MENU_SET2_MAIN_BLOCK_EXTRA +
    section_band_expanded;
  const collapsedHeaderHeight = MENU_SET2_COUNTERS_ROW_HEIGHT + section_band_collapsed;
  return { fullHeaderHeight, collapsedHeaderHeight };
}
